/**
 * Responsibility: produce deterministic pseudo-random numbers from a seed.
 *
 * This is the root of the determinism property described in the steering file. Every
 * random-looking value in the world traces back to a function here, which means the
 * whole world is reproducible from a single seed and nothing needs to be stored.
 *
 * Written by hand rather than pulling in `alea` because it is ten lines and the
 * Simple First Rule says a dependency is not worth it.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

/** Number of 32-bit values; used to normalise integer hashes into the 0..1 range. */
const UINT32_RANGE = 4294967296;

/** Odd 32-bit constant from the mulberry32 reference implementation. */
const MULBERRY32_INCREMENT = 0x6d2b79f5;

/**
 * Create a seeded pseudo-random number generator.
 *
 * mulberry32: tiny, fast, and statistically good enough for terrain. Not suitable for
 * anything security related, which does not matter here.
 *
 * @param {number} seed - Any integer. The same seed always yields the same sequence.
 * @returns {() => number} Function returning successive floats in [0, 1).
 */
export function createRng(seed) {
  let state = seed >>> 0;
  return function next() {
    state = (state + MULBERRY32_INCREMENT) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / UINT32_RANGE;
  };
}

/**
 * Hash a string into a 32-bit integer seed, so the player can type a memorable seed
 * like "sunset ridge" instead of a number.
 *
 * FNV-1a. Chosen for being short and well distributed over short strings.
 *
 * @param {string} str
 * @returns {number} Unsigned 32-bit integer.
 */
export function hashString(str) {
  const FNV_OFFSET_BASIS = 0x811c9dc5;
  const FNV_PRIME = 0x01000193;

  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME);
  }
  return hash >>> 0;
}

/**
 * Accept either a number or a string and return a usable integer seed.
 * Lets save files and the seed entry field share one code path.
 *
 * A string of digits is read as that number rather than hashed, so a seed the player
 * reads off a save or shares as text reproduces the same world when typed back in.
 * Without this, `1337` typed into a text field and `1337` stored as a number in a save
 * would generate two completely different worlds.
 *
 * Anything else — 'sunset ridge', an empty string, a non-finite number — is hashed.
 *
 * @param {number|string} seed
 * @returns {number} Unsigned 32-bit integer.
 */
export function normalizeSeed(seed) {
  if (typeof seed === 'number' && Number.isFinite(seed)) {
    return seed >>> 0;
  }

  if (typeof seed === 'string') {
    const trimmed = seed.trim();
    // Plain non-negative integers only. Anything with a sign, decimal point, or
    // exponent is treated as a phrase, because those forms have no single obvious
    // 32-bit reading and silently truncating them would be worse than hashing.
    if (/^\d+$/.test(trimmed)) {
      const parsed = Number(trimmed);
      if (Number.isSafeInteger(parsed)) {
        return parsed >>> 0;
      }
    }
    return hashString(trimmed);
  }

  return hashString(String(seed));
}

/**
 * Scramble a 32-bit integer so that flipping any single input bit changes roughly half
 * the output bits.
 *
 * This is the `lowbias32` finalizer. It exists because combining coordinates with XOR
 * alone is a *linear* operation: different (x, y) pairs collapse to the same value
 * disturbingly often, and negating both coordinates can map onto the original. In
 * practice that showed up as heavy hash collisions, which would have become visibly
 * repeating terrain and structure placement mirrored across the origin.
 *
 * @param {number} value
 * @returns {number} Unsigned 32-bit integer.
 */
function avalanche(value) {
  const MIX_A = 0x7feb352d;
  const MIX_B = 0x846ca68b;

  let v = value >>> 0;
  v ^= v >>> 16;
  v = Math.imul(v, MIX_A);
  v ^= v >>> 15;
  v = Math.imul(v, MIX_B);
  v ^= v >>> 16;
  return v >>> 0;
}

/**
 * Deterministically combine a seed and a 2D integer coordinate into a 32-bit hash.
 *
 * This is what makes per-location randomness reproducible without storing anything:
 * asking for the hash at the same tile always gives the same answer, so a city rebuilt
 * an hour later is identical.
 *
 * Coordinates are folded in one at a time with a large odd prime and an avalanche pass
 * after each. Mixing sequentially rather than symmetrically is what makes (3, 5) and
 * (5, 3) produce different results, so placement has no diagonal mirror pattern.
 *
 * @param {number} seed
 * @param {number} x - Integer coordinate.
 * @param {number} y - Integer coordinate.
 * @returns {number} Unsigned 32-bit integer.
 */
export function hashCoords(seed, x, y) {
  const X_MULTIPLIER = 0x27d4eb2d;
  const Y_MULTIPLIER = 0x165667b1;

  let h = seed >>> 0;
  h = avalanche((h + Math.imul(x | 0, X_MULTIPLIER)) >>> 0);
  h = avalanche((h + Math.imul(y | 0, Y_MULTIPLIER)) >>> 0);
  return h;
}

/**
 * Create an RNG scoped to a specific 2D location.
 *
 * Structure placement uses this: each tile (or each building slot) gets its own
 * independent stream, so generating tiles in a different order still produces an
 * identical world. Without per-location streams, world content would depend on visit
 * order, which would break determinism.
 *
 * @param {number} seed
 * @param {number} x - Integer coordinate.
 * @param {number} y - Integer coordinate.
 * @returns {() => number} Function returning successive floats in [0, 1).
 */
export function createLocalRng(seed, x, y) {
  return createRng(hashCoords(seed, x, y));
}

/**
 * Derive an independent seed from a base seed and a purpose label.
 *
 * Every noise field in the world must be statistically independent of every other one.
 * If the mountain elevation field and the city placement field shared a seed, cities
 * would always appear in the same places relative to peaks and the world would look
 * suspiciously correlated.
 *
 * Labels make that independence explicit and readable at the call site:
 *   deriveSeed(worldSeed, 'elevation')
 *   deriveSeed(worldSeed, 'biome:mountains')
 *
 * The same (seed, label) pair always derives the same value, so determinism holds.
 *
 * @param {number} seed - Base world seed.
 * @param {string} label - Stable identifier for what the derived field is used for.
 * @returns {number} Unsigned 32-bit integer.
 */
export function deriveSeed(seed, label) {
  return avalanche(((seed >>> 0) + hashString(label)) >>> 0);
}
